<script setup>
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

import DocumentService from '@/services/quality/document.service'

definePage({
  meta: {
    name: 'app-quality-document-archive',
    subject: 'document.menu',
    action: 'archive',
  },
})

const router = useRouter()
const toast = useToast()

const versions = ref([])
const total = ref(0)
const isFetching = ref(false)
const page = ref(1)
const itemsPerPage = ref(15)
const searchQuery = ref('')

const debouncedSearch = useDebounceFn(value => {
  searchQuery.value = value
  page.value = 1
}, 500)

const headers = [
  { title: 'Code', key: 'document.code', sortable: false },
  { title: 'Title', key: 'document.title', sortable: false },
  { title: 'Version', key: 'version_label', sortable: false },
  { title: 'Status', key: 'status', sortable: false },

  // Spec §F names this column: an assessor's question is "when did this stop
  // being current?", and without it the register cannot answer it.
  //
  // The resource never emits a unified `retired_at` — q_document_versions
  // carries separate superseded_at / withdrawn_at columns, and this endpoint
  // only ever returns SUPERSEDED or WITHDRAWN rows, so exactly one of the two
  // is populated per row. `retired_at` is a synthetic key resolved by the
  // #item.retired_at template below, not a field in the payload.
  { title: 'Retired', key: 'retired_at', sortable: false },
  { title: 'Type', key: 'document.type.name', sortable: false },
  { title: 'Department', key: 'document.department.name', sortable: false },

  // Consumes download_capability. Without this column Task 8 Step 9 pays for a
  // capability resolution on every page and nothing reads it — which is exactly
  // the dead-computation defect item A exists to fix. Do not drop it.
  { title: '', key: 'actions', sortable: false, align: 'end' },
]

let fetchToken = 0

const fetchDocuments = async () => {
  const token = ++fetchToken

  isFetching.value = true

  await DocumentService.listObsoleteVersions({
    search: searchQuery.value || undefined,
    per_page: itemsPerPage.value,
    page: page.value,
  }, data => {
    if (token !== fetchToken) return
    versions.value = data?.data ?? []
    total.value = data?.meta?.total ?? 0
  }, error => {
    // No global error net behind useApi. Without this the table spins forever
    // and, once viewArchive gates this endpoint, a 403 renders identically to
    // a genuinely empty archive ("0 archived versions"). The callback only
    // ever receives the parsed response body (DocumentService reads
    // useApi's error.value, which carries no HTTP status), so a permission
    // failure can't be reliably told apart from any other failure here —
    // surface one generic message rather than falsely claiming either.
    if (token !== fetchToken) return
    versions.value = []
    total.value = 0
    toast.error(error?.message ?? 'Could not load the archive.', 'error')
  })

  if (token === fetchToken) isFetching.value = false
}

// The file route is behind auth:api, so an <a href> or window.open would 401 —
// the browser does not attach the bearer token. Fetch the bytes and hand the
// user an object URL, mirroring DocumentReader's approach.
//
// The caller owns the object URL and must revoke it, or every file the user
// downloads leaks until the tab closes.
const download = async version => {
  const blob = await DocumentService.fetchFileBlob(version.document?.id, version.id)

  if (!blob) {
    // fetchFileBlob swallows its own error and returns null (it only logs to
    // the console) — without a toast here, a click on a row whose version
    // genuinely has no file (or whose fetch failed) does nothing visible at
    // all.
    toast.error('Could not download the file.', 'error')

    return
  }

  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')

  a.href = url
  a.download = `${version.document?.code ?? 'document'}-${version.version_label ?? ''}`
  a.click()
  URL.revokeObjectURL(url)
}

watch([page, itemsPerPage, searchQuery], fetchDocuments)

fetchDocuments()
</script>

<template>
  <div>
    <div class="d-flex align-center flex-wrap gap-3 mb-2">
      <div class="text-h5 flex-grow-1">
        Archive
      </div>
      <AppTextField
        placeholder="Search..."
        density="compact"
        style="inline-size: 260px;"
        @input="debouncedSearch($event.target.value)"
      />
    </div>

    <p class="text-body-2 text-medium-emphasis mb-6">
      Superseded and withdrawn versions are retained here permanently as evidence.
      Nothing is ever deleted. Opening one is recorded in the activity log.
    </p>

    <VCard
      variant="flat"
      class="border"
    >
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="page"
        :headers="headers"
        :items="versions"
        :items-length="total"
        :loading="isFetching"
        class="text-no-wrap"
        hover
        @click:row="(_, { item }) => router.push({ name: 'app-quality-document-id', params: { id: item.document?.id } })"
      >
        <template #item.status="{ item }">
          <VChip
            :color="item.status === 'WITHDRAWN' ? 'error' : 'secondary'"
            size="small"
            label
          >
            <span>{{ item.status === 'WITHDRAWN' ? 'Withdrawn' : 'Superseded' }}</span>
          </VChip>
        </template>

        <template #item.retired_at="{ item }">
          <span>{{ formatDate(item.superseded_at ?? item.withdrawn_at) }}</span>
        </template>

        <template #item.actions="{ item }">
          <!--
            The capability triple is the only authority for the PERMISSION
            question. `visible` decides whether the control belongs on the row;
            `allowed` decides whether it works; `reason` is user-facing copy.
            But DocumentPolicy::downloadObsolete() is a bare
            hasPermissionTo('quality.document.download_obsolete') check with no
            status or content check of its own — it is NOT state-aware. (The
            state-awareness for reading obsolete content lives one layer away,
            in DocumentContentGate.) So `visible` alone answers "may this user
            read obsolete content", never "is there anything to download" — an
            AUTHORED version carries no media file at all, and download_capability
            says nothing about that. Gate on has_file too, or the button renders
            and silently does nothing on an AUTHORED row.

            A VTooltip cannot attach directly to a :disabled IconBtn — browsers
            never fire mouseenter/mouseover on a disabled control, so the
            explanation would never show. Wrapping the button in a plain,
            non-disabled <span> gives the tooltip an activator that still
            receives hover events.
          -->
          <span
            v-if="item.download_capability?.visible && item.has_file"
            class="d-inline-block"
          >
            <IconBtn
              :disabled="!item.download_capability?.allowed"
              @click.stop="download(item)"
            >
              <VIcon icon="tabler-download" />
            </IconBtn>
            <VTooltip
              v-if="!item.download_capability?.allowed && item.download_capability?.reason"
              activator="parent"
              location="start"
            >
              {{ item.download_capability.reason }}
            </VTooltip>
          </span>
        </template>

        <template #bottom>
          <VDivider />
          <div class="d-flex align-center justify-sm-space-between justify-center flex-wrap gap-3 pa-5 pt-3">
            <p class="text-sm text-medium-emphasis mb-0">
              {{ total }} archived versions
            </p>
            <VPagination
              v-model="page"
              :length="Math.ceil(total / itemsPerPage) || 1"
              :total-visible="5"
            />
          </div>
        </template>
      </VDataTableServer>
    </VCard>
  </div>
</template>
